'use client';

import { ICartGroup, ICartItem, ITotalData } from '@/api/types/cart';
import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import ZeeloolCheckBox from '@/components/ZeeloolCheckBox';
import { EPlatform } from '@/utils/enums/platform-enum';
import { setSession } from '@/utils/tools';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { CartEmpty } from './CartEmpty';
import { CartItem } from './cartItem/CartItem';
import { CartRight } from './CartRight';
import InvalidCartItem from './InvalidCartItem/InvalidCartItem';
import NoticeBar from './NoticeBar';

interface CartPagePCProps {
  // 购物车数据
  cartCanBuyList: ICartGroup[];
  cartCanNotBuyList: ICartItem[];

  // 状态
  allCheck: boolean;
  batchFlag: boolean;
  batchDisabled: boolean;
  checkoutLoading: boolean;
  showMoreInvalid: boolean;

  // 计算数据
  totalData: ITotalData;
  allQty: number;
  selectedCanBuy: ICartItem[];

  // 配置数据
  currency: any;
  halfPriceConfig: any;
  maxSaving: string;

  // 广告和推荐数据
  showAdsImg: boolean;
  advertObj: any;
  advertData: any;
  recommendList: any;
  freeShipData: any;

  // 事件回调
  onCheckChange: () => void;
  onBatchFlagChange: (flag: boolean) => void;
  onSelectChange: (index: number, checked: boolean) => void;
  onReloadCart: (cartData?: any) => Promise<void>;
  onMoveWish: () => void;
  onBatDelete: () => void;
  onBatchCancel: () => void;
  onClearAll: () => Promise<void>;
  onCheckout: () => Promise<void>;
  onShowMoreInvalidChange: (show: boolean) => void;

  // 埋点回调
  onCartPageBannerClick?: () => void;
  onCartPageBannerExp?: () => void;

  // 促销相关
  getItemPromotion: (cartId: number) => any;

  // 其他回调
  onInvalidSelectChange: (index: number, checked: boolean) => void;
}

export function CartPagePC({
  cartCanBuyList,
  cartCanNotBuyList,
  allCheck,
  batchFlag,
  batchDisabled,
  checkoutLoading,
  showMoreInvalid,
  totalData,
  allQty,
  selectedCanBuy,
  currency,
  halfPriceConfig,
  maxSaving,
  showAdsImg,
  advertObj,
  advertData,
  recommendList,
  freeShipData,
  onCheckChange,
  onBatchFlagChange,
  onSelectChange,
  onReloadCart,
  onMoveWish,
  onBatDelete,
  onBatchCancel,
  onClearAll,
  onCheckout,
  onShowMoreInvalidChange,
  onCartPageBannerClick,
  onCartPageBannerExp,
  getItemPromotion,
  onInvalidSelectChange,
}: CartPagePCProps) {
  const t = useTranslations('cart.cartPage');
  // 广告位曝光埋点
  const [bannerRef, bannerInView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  // 处理广告位曝光埋点
  useEffect(() => {
    if (bannerInView && onCartPageBannerExp && showAdsImg && advertObj) {
      try {
        onCartPageBannerExp();
      } catch {
        // 不处理
      }
    }
  }, [bannerInView, onCartPageBannerExp, showAdsImg, advertObj]);

  // 处理广告位点击埋点
  const handleBannerClick = useCallback(() => {
    if (onCartPageBannerClick) {
      setSession('zl_goodsListEnterPageScene', '购物车页广告位');
      try {
        onCartPageBannerClick();
      } catch {
        // 不处理
      }
    }
  }, [onCartPageBannerClick]);
  return (
    <div className="w-full bg-[#f5f6f7]">
      <div className="mx-auto">
        <div className="flex justify-center pb-[30px]">
          {/* 左侧内容区域 */}
          <div className=" w-[761px] 2xl:w-[886px]">
            {cartCanBuyList.length > 0 && (
              <>
                {/* PC端标题 */}
                <div className="h-[84px] 2xl:h-[100px] leading-[84px] 2xl:leading-[100px] text-[28px] font-bb">
                  {t('shoppingCart')}
                </div>

                {/* PC端广告位 */}
                {showAdsImg && (
                  <div className="mb-[16px]" ref={bannerRef}>
                    <MagicJump item={advertObj} onBeforeJump={handleBannerClick}>
                      <Image
                        src={advertData.cart_settlement_pc_image}
                        alt="Advertisement"
                        width={886}
                        height={50}
                        className="w-full h-auto block"
                      />
                    </MagicJump>
                  </div>
                )}

                {/* PC端NoticeBar */}
                <NoticeBar
                  recommendList={recommendList}
                  freeShipData={freeShipData}
                  totalData={totalData}
                  batchFlag={batchFlag}
                  deviceType={EPlatform.WEB}
                />

                {/* PC端操作栏 */}
                <div className="flex items-center justify-between mb-[20px]">
                  <div className="flex items-center">
                    <ZeeloolCheckBox
                      checked={allCheck}
                      onChange={() => onCheckChange()}
                      fontSize={20}
                    />
                    <span className="ml-[10px] text-[16px] font-bb">
                      {t('allItem')} ({allQty})
                    </span>
                  </div>

                  {!batchFlag ? (
                    <span
                      onClick={() => onBatchFlagChange(true)}
                      className="text-[16px] cursor-pointer"
                    >
                      {t('massDelete')}
                    </span>
                  ) : (
                    <div className="flex items-center gap-[30px]">
                      <span
                        onClick={onMoveWish}
                        className={`text-primary-purple underline cursor-pointer ${batchDisabled ? '!text-[#dddddd] cursor-not-allowed' : ''}`}
                      >
                        {t('moveToWishlistBatch')}
                      </span>
                      <span
                        onClick={onBatDelete}
                        className={`text-primary-purple underline cursor-pointer ${batchDisabled ? '!text-[#dddddd] cursor-not-allowed' : ''}`}
                      >
                        {t('deleteSelectedItems')}
                      </span>
                      <span onClick={onBatchCancel} className="text-[16px] cursor-pointer">
                        {t('cancel')}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* PC端购物车列表 */}
            <div className="space-y-8">
              {cartCanBuyList.map((item, index) => (
                <div key={index} className="relative bg-white mb-[30px]">
                  <div className="flex items-start">
                    <div className="flex-1">
                      {item.carts?.map((cartItem) => (
                        <CartItem
                          key={cartItem.cart_id}
                          cartItemData={cartItem}
                          batchFlag={batchFlag}
                          onReload={onReloadCart}
                          selected={item.selected || false}
                          itemDisabled={item.disabled || false}
                          onSelectChange={(checked) => onSelectChange(index, checked)}
                          itemType={item.type || 1}
                          itemPromotion={getItemPromotion(cartItem.cart_id || 0)}
                          currency={currency}
                          giftCountDown={item.count_down || 0}
                          giftAllowType={item.is_allow_settlement || 0}
                          itemGiftInfo={item.relationable || {}}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {cartCanBuyList.length === 0 && cartCanNotBuyList.length > 0 && (
              <CartEmpty deviceType={EPlatform.WEB} className="!pb-[40px]" />
            )}

            {/* PC端无效商品区域 */}
            {cartCanNotBuyList.length > 0 && (
              <div
                className={`mt-8 bg-white rounded-[12px] overflow-hidden ${cartCanBuyList.length === 0 ? 'mb-[66px]' : ''}`}
              >
                <div
                  className={`flex items-center justify-between p-4 bg-white h-[69px] px-[16px]`}
                >
                  <span className="text-[16px] font-br">
                    {cartCanNotBuyList.length > 1
                      ? t('outOfStockInvalidItemsPlural')
                      : t('outOfStockInvalidItems')}
                    ({cartCanNotBuyList.length})
                  </span>
                  <div className="flex items-center space-x-4">
                    {!batchFlag && showMoreInvalid && (
                      <button
                        onClick={onClearAll}
                        className="text-[14px] font-br underline text-[#282828] cursor-pointer"
                      >
                        {t('clearAll')}
                      </button>
                    )}
                    {!showMoreInvalid && (
                      <button
                        onClick={() => onShowMoreInvalidChange(!showMoreInvalid)}
                        className="text-[14px] cursor-pointer flex items-center"
                      >
                        {t('viewAll')}
                        <CollapseArrowDownOutlined className="text-[18px] ml-[5px] text-[#808080]" />
                      </button>
                    )}
                  </div>
                </div>

                {showMoreInvalid && (
                  <div className="bg-white">
                    {cartCanNotBuyList.map((item, index) => (
                      <div key={index} className="relative border-b border-[#eeeeee] last:border-b">
                        <div className={`flex-1`}>
                          <InvalidCartItem
                            cartItemData={item}
                            batchFlag={batchFlag}
                            onReload={onReloadCart}
                            itemIndex={index}
                            currency={currency}
                            selected={item.selected || false}
                            onInvalidSelectChange={(checked) =>
                              onInvalidSelectChange(index, checked)
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 右侧结算区域 */}
          {cartCanBuyList.length > 0 && (
            <div className="w-[432px] 2xl:w-[502px] ml-[29px] 2xl:ml-[48px]">
              <div className="sticky top-[100px]">
                <div className="h-[84px] 2xl:h-[100px]" />
                <CartRight
                  selectedItems={selectedCanBuy}
                  totalData={totalData}
                  checkoutLoading={checkoutLoading}
                  onCheckout={onCheckout}
                  halfPriceData={halfPriceConfig}
                  maxSaving={maxSaving}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
